import { useEffect, useRef } from 'react';
import { Icone } from '../components/Icone';
import { OPTIONS_ACCESSIBILITE } from './options';
import { useAccessibilite } from './useAccessibilite';

/**
 * Bouton flottant et panneau d'adaptations, reprise de la maquette validée.
 *
 * Il est présent sur tous les écrans : une personne qui a besoin d'agrandir le
 * texte en a besoin partout, pas seulement sur l'écran « Accessibilité » — qui
 * l'ouvre aussi, par ses boutons, comme dans la maquette.
 *
 * Au clavier (TF-341) : le panneau suit son bouton dans l'ordre de tabulation,
 * la première option prend le focus à l'ouverture, et `Échap` ou « Fermer »
 * le rendent à ce qui l'avait avant. C'est un dialogue non modal : la page
 * reste utilisable, un clic à côté le ferme.
 */
export function PanneauAccessibilite(): JSX.Element {
  const {
    etat,
    basculer,
    reinitialiser,
    nombreActives,
    lectureVocaleDisponible,
    panneauOuvert: ouvert,
    ouvrirPanneau,
    fermerPanneau,
  } = useAccessibilite();
  const conteneur = useRef<HTMLDivElement>(null);
  const premiereOption = useRef<HTMLButtonElement>(null);
  const declencheur = useRef<HTMLElement | null>(null);

  // À l'ouverture, d'où qu'elle vienne : on retient ce qui avait le focus et
  // on le donne à la première option.
  useEffect(() => {
    if (!ouvert) return;
    declencheur.current = document.activeElement as HTMLElement | null;
    premiereOption.current?.focus();
  }, [ouvert]);

  const fermerEtRendreFocus = (): void => {
    fermerPanneau();
    declencheur.current?.focus?.();
  };

  // Fermeture au clic extérieur et à `Échap`, comme dans la maquette.
  useEffect(() => {
    if (!ouvert) return;

    const auClicExterieur = (evenement: MouseEvent): void => {
      if (!conteneur.current?.contains(evenement.target as Node)) fermerPanneau();
    };
    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key !== 'Escape') return;
      fermerPanneau();
      declencheur.current?.focus?.();
    };

    document.addEventListener('mousedown', auClicExterieur);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClicExterieur);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert, fermerPanneau]);

  // Position de la maquette : 18 px du bord gauche, 64 px du bas. Dès `lg`, la
  // barre latérale est à l'écran et lui réserve une bande de 56 px au-dessus de
  // son bloc utilisateur de 80 px (TF-491) : il s'y pose (88 px du bas, 40 px
  // de haut), sans plus recouvrir les dernières entrées du menu.
  return (
    <div ref={conteneur} className="panneau-accessibilite fixed bottom-16 left-[18px] z-[110] print:hidden lg:bottom-[88px]">
      <button
        type="button"
        onClick={() => (ouvert ? fermerPanneau() : ouvrirPanneau())}
        aria-expanded={ouvert}
        aria-controls="panneau-adaptations"
        aria-label={`Options d’accessibilité${nombreActives > 0 ? ` · ${nombreActives} active(s)` : ''}`}
        className="flex items-center gap-2 rounded-2xl bg-codialis-blue p-3 text-[13px] font-semibold text-white shadow-[0_18px_40px_-12px_rgba(11,31,58,.5)] transition hover:-translate-y-0.5 sm:px-4 sm:py-2.5"
      >
        <svg aria-hidden="true" className="h-5 w-5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <circle cx="12" cy="4" r="1.6" fill="currentColor" stroke="none" />
          <path strokeLinecap="round" d="M4 8h16M9 8v5l-2 7m8-12v5l2 7" />
        </svg>
        {/* Sur téléphone le bouton se réduit à son icône : posé par-dessus le
            contenu, il masquait une ligne de liste sur toute sa largeur.
            Le libellé reste porté par `aria-label`. */}
        <span className="hidden sm:inline">Accessibilité</span>
        {nombreActives > 0 && (
          <span className="rounded bg-codialis-mint px-1.5 py-0.5 text-[10px] font-bold text-codialis-navy">
            {nombreActives}
          </span>
        )}
      </button>

      {ouvert && (
        <div
          id="panneau-adaptations"
          role="dialog"
          aria-labelledby="titre-panneau-adaptations"
          className="absolute bottom-14 left-0 w-80 max-w-[90vw] overflow-hidden rounded-2xl bg-white shadow-[0_30px_70px_-18px_rgba(11,31,58,.4)]"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h2 id="titre-panneau-adaptations" className="font-display text-sm font-bold text-codialis-navy">
              Accessibilité · RGAA AA
            </h2>
            <button
              type="button"
              onClick={fermerEtRendreFocus}
              aria-label="Fermer"
              className="rounded p-1 text-codialis-gray transition hover:text-codialis-navy"
            >
              <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="scrollbar-thin max-h-[60vh] overflow-y-auto py-1">
            {OPTIONS_ACCESSIBILITE.map((option, index) => {
              const indisponible = option.cle === 'tts' && !lectureVocaleDisponible;
              const actif = etat[option.cle] === true;
              return (
                <button
                  key={option.cle}
                  ref={index === 0 ? premiereOption : undefined}
                  type="button"
                  role="switch"
                  aria-checked={actif}
                  disabled={indisponible}
                  onClick={() => basculer(option.cle)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-[#f4f7fc] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex items-center gap-3">
                    <Icone nom={option.icone} className="h-5 w-5 text-codialis-sky" />
                    <span>
                      <span className="block text-sm font-medium text-codialis-navy">
                        {option.libelle}
                      </span>
                      <span className="block text-[11px] text-codialis-gray">
                        {indisponible
                          ? 'Non disponible dans ce navigateur'
                          : option.detail}
                      </span>
                    </span>
                  </span>
                  <Bascule actif={actif} />
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={reinitialiser}
            disabled={nombreActives === 0}
            className="w-full border-t border-gray-100 py-2.5 text-sm font-medium text-codialis-gray transition hover:bg-gray-50 disabled:opacity-50"
          >
            Tout réinitialiser
          </button>
        </div>
      )}
    </div>
  );
}

function Bascule({ actif }: { actif: boolean }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`relative h-[22px] w-[38px] shrink-0 rounded-full transition ${
        actif ? 'bg-codialis-mint' : 'bg-[#cdd7e5]'
      }`}
    >
      <span
        className={`absolute left-[3px] top-[3px] h-4 w-4 rounded-full bg-white transition ${
          actif ? 'translate-x-4' : ''
        }`}
      />
    </span>
  );
}
